import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, AdminPanelSettings, Edit, Save, People, Analytics, MonetizationOn, Folder, TrendingUp, Download, PictureAsPdf, TableChart } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../contexts/LanguageContext';
import { adminService } from '../services/admin';
import { AdminUserSummary, StatisticsResponse, GlobalStatisticsResponse, TokenTransaction } from '../types';
import { 
  exportUsersToExcel, 
  exportUsersToPDF, 
  exportGlobalStatsToExcel, 
  exportGlobalStatsToPDF,
  exportUserStatsToExcel,
  exportUserStatsToPDF,
  exportTokenHistoryToExcel,
  exportTokenHistoryToPDF
} from '../utils/exportUtils';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuthStore();
  const { t } = useLanguage();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStatisticsResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(null);
  const [stats, setStats] = useState<StatisticsResponse | null>(null);
  const [tokenHistory, setTokenHistory] = useState<TokenTransaction[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({
    email: '',
    name: '',
    rolesText: '',
    newPassword: '',
    tokensToAdd: '',
  });

  // Kullanıcı yüklenene kadar bekle (max 3 saniye)
  const [userLoadTimeout, setUserLoadTimeout] = useState(false);
  useEffect(() => {
    if (authLoading) {
      const timer = setTimeout(() => {
        setUserLoadTimeout(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setUserLoadTimeout(false);
    }
  }, [authLoading]);

  // Kullanıcı yoksa, token'ı temizle ve login'e yönlendir
  useEffect(() => {
    if (!authLoading && !user && isAuthenticated) {
      // Token var ama kullanıcı bilgileri yüklenemiyor, token'ı temizle
      localStorage.removeItem('access_token');
      navigate('/login');
    }
  }, [authLoading, user, isAuthenticated, navigate]);

  // Eğer kullanıcı admin değilse dashboard'a yönlendir (sadece user yüklendikten sonra)
  useEffect(() => {
    // authLoading false olduğunda ve user varsa kontrol et
    if (!authLoading && user) {
      const roles = user.roles;
      let hasAdminRole = false;
      if (roles && Array.isArray(roles)) {
        hasAdminRole = roles.includes('admin');
      }
      
      if (!hasAdminRole) {
        console.warn('AdminPage: User does not have admin role, redirecting to dashboard');
        console.warn('User roles:', roles, 'Type:', typeof roles, 'IsArray:', Array.isArray(roles));
        navigate('/dashboard');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Sadece kullanıcı admin ise verileri yükle
    if (!user) return;
    
    const roles = user.roles;
    let hasAdminRole = false;
    if (roles && Array.isArray(roles)) {
      hasAdminRole = roles.includes('admin');
    }
    
    if (!hasAdminRole) {
      return;
    }

    const load = async () => {
      setLoading(true);
      setLoadingStats(true);
      try {
        const [list, global] = await Promise.all([
          adminService.listUsers(),
          adminService.getGlobalStatistics(),
        ]);
        setUsers(list);
        setGlobalStats(global);
      } catch (err: any) {
        console.error('Admin verileri alınamadı:', err);
        alert(t.userListFailed + ': ' + (err?.response?.data?.detail || err?.message));
      } finally {
        setLoading(false);
        setLoadingStats(false);
      }
    };
    load();
  }, [user, t.userListFailed]);

  const handleSelectUser = async (u: AdminUserSummary) => {
    setSelectedUser(u);
    setFormState({
      email: u.email || '',
      name: u.name || '',
      rolesText: (u.roles || []).join(', '),
      newPassword: '',
      tokensToAdd: '',
    });
    setLoadingHistory(true);
    try {
      const [s, history] = await Promise.all([
        adminService.getUserStatistics(u.username),
        adminService.getUserTokenHistory(u.username, 50),
      ]);
      setStats(s);
      setTokenHistory(history);
    } catch (err: any) {
      console.error(t.statsFailed + ':', err);
      setStats(null);
      setTokenHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const roles = formState.rolesText
        .split(',')
        .map(r => r.trim())
        .filter(Boolean);

      const tokensToAdd = formState.tokensToAdd ? parseInt(formState.tokensToAdd, 10) : undefined;
      if (tokensToAdd !== undefined && (isNaN(tokensToAdd) || tokensToAdd < 0)) {
        alert('Token miktarı geçerli bir sayı olmalıdır (0 veya pozitif).');
        setSaving(false);
        return;
      }

      const updated = await adminService.updateUser(selectedUser.username, {
        email: formState.email || undefined,
        name: formState.name || undefined,
        roles: roles.length ? roles : undefined,
        new_password: formState.newPassword || undefined,
        tokens_to_add: tokensToAdd,
      });

      // Listeyi güncelle ve token history'yi yeniden yükle
      setUsers(prev =>
        prev.map(u => (u.username === updated.username ? updated : u))
      );
      setSelectedUser(updated);
      
      // Token eklendiyse, token history'yi yeniden yükle
      if (tokensToAdd && tokensToAdd > 0) {
        try {
          const history = await adminService.getUserTokenHistory(selectedUser.username, 50);
          setTokenHistory(history);
        } catch (err) {
          console.error('Token history yüklenemedi:', err);
        }
      }
      
      setFormState(fs => ({ ...fs, newPassword: '', tokensToAdd: '' }));
      alert(tokensToAdd && tokensToAdd > 0 
        ? `${tokensToAdd} token eklendi. ${t.userUpdated}` 
        : t.userUpdated);
    } catch (err: any) {
      console.error('Kullanıcı güncellenemedi:', err);
      alert(t.userUpdateFailed + ': ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setSaving(false);
    }
  };

  // Early returns - tüm hook'lardan sonra
  if (authLoading && !userLoadTimeout) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">{t.loading}...</div>
      </div>
    );
  }

  // Kullanıcı yoksa loading göster (yönlendirme useEffect'te yapılıyor)
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">{t.loading}...</div>
      </div>
    );
  }

  // Debug: user roles'u console'a yazdır
  console.log('AdminPage - Current user:', JSON.stringify(user, null, 2));
  console.log('AdminPage - User roles:', user.roles);
  console.log('AdminPage - Roles type:', typeof user.roles);
  console.log('AdminPage - Is array?', Array.isArray(user.roles));
  console.log('AdminPage - Is admin?', user.roles?.includes('admin'));

  // Check if user has admin role
  const roles = user.roles;
  let hasAdminRole = false;
  if (roles && Array.isArray(roles)) {
    hasAdminRole = roles.includes('admin');
  }

  if (!hasAdminRole) {
    // Yönlendirme useEffect'te yapılıyor, burada sadece mesaj göster
    console.warn('AdminPage - User does not have admin role. Roles:', user.roles);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 dark:text-gray-400 mb-4">
            Admin yetkisi gerekli. Kullanıcı rolleri: {user.roles ? (Array.isArray(user.roles) ? user.roles.join(', ') : String(user.roles)) : 'Yok'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Debug: roles type = {typeof user.roles}, isArray = {Array.isArray(user.roles) ? 'true' : 'false'}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowBack style={{ width: '16px', height: '16px' }} />
          <span>{t.back}</span>
        </button>

        <div className="flex items-center space-x-3 mb-6">
          <AdminPanelSettings style={{ width: '32px', height: '32px' }} className="text-gray-900 dark:text-white" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t.adminPanel}</h1>
        </div>

        {/* Global Statistics Dashboard */}
        {globalStats && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Global Statistics</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => exportGlobalStatsToExcel(globalStats)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
                  title="Export to Excel"
                >
                  <TableChart style={{ width: '16px', height: '16px' }} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => exportGlobalStatsToPDF(globalStats)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                  title="Export to PDF"
                >
                  <PictureAsPdf style={{ width: '16px', height: '16px' }} />
                  <span>PDF</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <People style={{ width: '24px', height: '24px' }} className="text-gray-700 dark:text-gray-300" />
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Total Users</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{globalStats.total_users}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{globalStats.active_users} active</div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <Folder style={{ width: '24px', height: '24px' }} className="text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Total Projects</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{globalStats.total_projects}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {globalStats.avg_projects_per_user.toFixed(1)} per user
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <MonetizationOn style={{ width: '24px', height: '24px' }} className="text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tokens</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {(globalStats.total_tokens_remaining ?? 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(globalStats.total_tokens_used ?? 0).toLocaleString()} used
              </div>
            </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Kullanıcı listesi */}
          <div className="md:col-span-1 bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                {t.users} ({users.length})
              </h2>
              <div className="flex gap-1">
                <button
                  onClick={() => exportUsersToExcel(users)}
                  className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors"
                  title="Export to Excel"
                >
                  <TableChart style={{ width: '14px', height: '14px' }} />
                </button>
                <button
                  onClick={() => exportUsersToPDF(users)}
                  className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  title="Export to PDF"
                >
                  <PictureAsPdf style={{ width: '14px', height: '14px' }} />
                </button>
                {loading && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t.loading}...</span>
                )}
              </div>
            </div>
            <div className="space-y-1 max-h-[600px] overflow-auto custom-scrollbar">
              {users.map(u => (
                <button
                  key={u.username}
                  onClick={() => handleSelectUser(u)}
                  className={`w-full text-left px-3 py-3 rounded-lg text-sm border transition-colors ${
                    selectedUser?.username === u.username
                      ? 'border-gray-900 dark:border-gray-100 bg-gray-900/5 dark:bg-gray-100/5'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {u.name || u.username}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {u.total_projects} {t.project}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {u.email || t.noEmail}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 text-xs">
                      <MonetizationOn style={{ width: '14px', height: '14px' }} className="text-amber-600 dark:text-amber-400" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {(u.tokens_remaining ?? 0).toLocaleString()}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/</span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {(u.tokens_used ?? 0).toLocaleString()} used
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(u.roles || ['user']).map(role => (
                      <span
                        key={role}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          role === 'admin'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
              {users.length === 0 && !loading && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t.noUsers}
                </p>
              )}
            </div>
          </div>

          {/* Kullanıcı düzenleme ve istatistikler */}
          <div className="md:col-span-2 space-y-6">
            {/* User Info Card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                  {t.userInfo}
                </h2>
              </div>

              {selectedUser ? (
                <div className="space-y-4">
                  {/* Token Info */}
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase mb-1">
                          Token Usage
                        </div>
                        <div className="flex items-baseline space-x-2">
                          <span className="text-2xl font-bold text-amber-900 dark:text-amber-200">
                            {(selectedUser.tokens_remaining ?? 0).toLocaleString()}
                          </span>
                          <span className="text-sm text-amber-700 dark:text-amber-400">remaining</span>
                        </div>
                        <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          {(selectedUser.tokens_used ?? 0).toLocaleString()} tokens used
                        </div>
                      </div>
                      <MonetizationOn style={{ width: '32px', height: '32px' }} className="text-amber-600 dark:text-amber-400" />
                    </div>
                  </div>

                  {/* User Registration Info */}
                  {selectedUser.created_at && (
                    <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                        Member Since
                      </div>
                      <div className="text-sm text-gray-900 dark:text-gray-200">
                        {new Date(selectedUser.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                      <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                        {(() => {
                          const days = Math.floor((Date.now() - new Date(selectedUser.created_at!).getTime()) / (1000 * 60 * 60 * 24));
                          if (days < 30) return `${days} days ago`;
                          const months = Math.floor(days / 30);
                          if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
                          const years = Math.floor(months / 12);
                          return `${years} year${years > 1 ? 's' : ''} ago`;
                        })()}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t.username}
                    </label>
                    <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-sm text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-800">
                      {selectedUser.username}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {t.fullName}
                      </label>
                      <div className="flex items-center space-x-2">
                        <Edit style={{ width: '16px', height: '16px' }} className="text-gray-400" />
                        <input
                          type="text"
                          value={formState.name}
                          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {t.email}
                      </label>
                      <div className="flex items-center space-x-2">
                        <Edit style={{ width: '16px', height: '16px' }} className="text-gray-400" />
                        <input
                          type="email"
                          value={formState.email}
                          onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t.roles} ({t.rolesHint})
                    </label>
                    <textarea
                      value={formState.rolesText}
                      onChange={(e) => setFormState({ ...formState, rolesText: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Add Tokens (Admin)
                    </label>
                    <div className="flex items-center space-x-2">
                      <MonetizationOn style={{ width: '16px', height: '16px' }} className="text-amber-600 dark:text-amber-400" />
                      <input
                        type="number"
                        min="0"
                        value={formState.tokensToAdd}
                        onChange={(e) => setFormState({ ...formState, tokensToAdd: e.target.value })}
                        placeholder="0"
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-600 dark:focus:ring-amber-400"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enter number of tokens to add to this user's account
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t.newPassword} ({t.leaveBlankForNoChange})
                    </label>
                    <input
                      type="password"
                      value={formState.newPassword}
                      onChange={(e) => setFormState({ ...formState, newPassword: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save style={{ width: '16px', height: '16px' }} />
                      <span>{saving ? t.saving : t.save}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.selectUserFromLeft}
                </p>
              )}
            </div>

            {/* User Statistics Card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Analytics style={{ width: '20px', height: '20px' }} className="text-gray-600 dark:text-gray-400" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    {t.userStatistics}
                  </h2>
                </div>
                {selectedUser && stats && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => exportUserStatsToExcel(stats, selectedUser.username)}
                      className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors"
                      title="Export to Excel"
                    >
                      <TableChart style={{ width: '14px', height: '14px' }} />
                    </button>
                    <button
                      onClick={() => exportUserStatsToPDF(stats, selectedUser.username)}
                      className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                      title="Export to PDF"
                    >
                      <PictureAsPdf style={{ width: '14px', height: '14px' }} />
                    </button>
                  </div>
                )}
              </div>

              {selectedUser ? (
                stats ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-semibold">
                        {t.totalProjects}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.total_projects}
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-semibold">
                        {t.averageScore}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.average_score != null ? stats.average_score.toFixed(1) : '-'}
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-semibold">
                        {t.highestScore}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.highest_score != null ? stats.highest_score.toFixed(1) : '-'}
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-semibold">
                        {t.lowestScore}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.lowest_score != null ? stats.lowest_score.toFixed(1) : '-'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t.statsNotFound}
                  </p>
                )
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.statsWillAppear}
                </p>
              )}
            </div>

            {/* Token History Card */}
            {selectedUser && (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <MonetizationOn style={{ width: '20px', height: '20px' }} className="text-amber-600 dark:text-amber-400" />
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      Token History
                    </h2>
                  </div>
                  {tokenHistory.length > 0 && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => exportTokenHistoryToExcel(tokenHistory)}
                        className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors"
                        title="Export to Excel"
                      >
                        <TableChart style={{ width: '14px', height: '14px' }} />
                      </button>
                      <button
                        onClick={() => exportTokenHistoryToPDF(tokenHistory)}
                        className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                        title="Export to PDF"
                      >
                        <PictureAsPdf style={{ width: '14px', height: '14px' }} />
                      </button>
                    </div>
                  )}
                </div>

                {loadingHistory ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
                ) : tokenHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Type</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Amount</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Before</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokenHistory.map((tx) => (
                          <tr key={tx.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                              {new Date(tx.created_at).toLocaleString('tr-TR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td className="py-2 px-3">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                tx.transaction_type === 'purchase'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}>
                                {tx.transaction_type === 'purchase' ? 'Purchase' : 'Usage'}
                              </span>
                            </td>
                            <td className={`py-2 px-3 text-right font-medium ${
                              tx.amount > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                              {tx.balance_before.toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-right font-semibold text-gray-900 dark:text-white">
                              {tx.balance_after.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No token transactions found</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
